import React, {useState} from 'react';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import ProgressBar from '../../ProgressBar';
import ButtonShare from '../ButtonShare';
import {ReactComponent as CloseIcon} from '../../../assets/close.svg';
import {Link, useHistory} from 'react-router-dom';
import {notWebMediaQuery, useIsWeb} from '../../../styles';
import ImageArrow from '../../../assets/ic-comics-next.png';
import {useSelector} from 'react-redux';
import {getCurrentLang} from '../../../utils/i18n';

const ReaderCollapsableHeader = props => {
    const [height, setHeight] = useState(215);
    const [maxHeight, setMaxHeight] = useState(215);
    const {t} = useTranslation();
    const history = useHistory();

    useEffect(() => {
        if (props.collapsed) {
            setHeight(5);
            setMaxHeight(90);
        } else {
            setHeight(maxHeight);
        }
    }, [props.collapsed, height, maxHeight]);

    const user = useSelector(state => state.user);
    const idp = user && user.idp;

    const trackExitState = () => {
        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                interaction: 'comic|exit',
                domain: 'cnplus.com',
                brand: 'cartoon network',
                server: 'cnplus',
                platform: 'web',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                interactionevent: '1',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                userlang: getCurrentLang(),
                franchise: props.comic.show,
                contenttype: 'comics',
                contenttitle: props.comic.title,
                progress: String(
                    Math.min(10, Math.round((props.progress * 100) / 10)),
                ),
                chapter_number: String(props.chapter.number),
                chapter_total: String(props.comic.chapters.length),
                pages_total: '10',
                english_name: props.comic.englishTitle.toLowerCase(),
                comicexit_event: '1',
            },
        });
    };

    const trackNext = () => {
        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                interaction: 'comic|next chapter',
                domain: 'cnplus.com',
                brand: 'cartoon network',
                server: 'cnplus',
                platform: 'web',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                interactionevent: '1',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                userlang: getCurrentLang(),
                franchise: props.comic.show,
                contenttype: 'comics',
                contenttitle: props.comic.title,
                chapter_number: String(props.chapter.number),
                english_name: props.comic.englishTitle.toLowerCase(),
                nextchapter_event: '1',
            },
        });
    };

    const close = () => {
        trackExitState();

        if (history.length > 2) {
            history.goBack();
        } else {
            window.location = '/comics';
        }
    };

    let isWeb = useIsWeb();

    return (
        <Container height={height}>
            <RelativeContainer>
                <TitleContainer>
                    {isWeb && (
                        <ImagePrev
                            enabled={props.chapter.number > 1}
                            style={
                                props.chapter.number > 1
                                    ? null
                                    : {pointerEvents: 'none'}
                            }
                            to={`/comics/${props.comic.friendlyName}/${props
                                .chapter.number - 1}`}
                            replace
                        />
                    )}
                    <Text>
                        {t('comics.chapter')} {props.chapter.number}
                    </Text>

                    {isWeb && (
                        <ImageNext
                            enabled={
                                props.chapter.number <
                                props.comic.chapters.length
                            }
                            style={
                                props.chapter.number <
                                props.comic.chapters.length
                                    ? null
                                    : {pointerEvents: 'none'}
                            }
                            to={`/comics/${props.comic.friendlyName}/${props
                                .chapter.number + 1}`}
                            replace
                            onClick={trackNext}
                        />
                    )}
                </TitleContainer>
                <BackButton onClick={close} bottom={maxHeight - 74}>
                    <CloseIcon />
                </BackButton>
                <StyledShare
                    iconOnly
                    comic={props.comic}
                    chapter={props.chapter}
                    bottom={maxHeight - 74}
                />

                <Logo
                    src={`${props.comic.baseUrl}/${props.comic.logo}?w=160`}
                    opacity={maxHeight > 90 ? 1 : 0}
                />

                <StyledBar percent={props.progress} color="#EC098C" />
            </RelativeContainer>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    align-items: center;
    height: ${props => props.height}px;
    position: absolute;
    display: flex;
    z-index: 1;
    background-color: black;
    transition: height 0.2s;
`;

const RelativeContainer = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
`;

const Text = styled.span`
    font-family: TTNormsProExtraBlack;
    font-style: normal;
    font-size: 20px;
    line-height: 24px;
    text-align: center;
    text-transform: uppercase;
    color: white;
`;

const Logo = styled.img`
    position: absolute;
    bottom: 50px;
    width: 160px;
    height: 160px;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    opacity: ${props => props.opacity};
    transition: opacity 0.2s;
`;

const StyledBar = styled(ProgressBar)`
    position: absolute;
    bottom: 0px;
`;

const BackButton = styled.div`
    position: absolute;
    bottom: ${props => props.bottom}px;
    left: 20px;
    cursor: pointer;

    svg {
        width: 34px;
        height: 34px;
        g {
            fill: #fff;
        }
    }
`;

const TitleContainer = styled.span`
    display: flex;
    flex-direction: row;
    position: absolute;
    bottom: 16px;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    align-items: center;
    justify-content: center;
    ${notWebMediaQuery} {
        bottom: 24px;
    }
`;

const StyledShare = styled(ButtonShare)`
    position: absolute;
    bottom: ${props => props.bottom}px;
    right: 10px;
`;

const ImageNext = styled(Link)`
    background-image: url(${ImageArrow});
    background-repeat: no-repeat;
    background-position: center;
    opacity: ${props => (props.enabled ? 1 : 0.5)};
    margin: 0px 80px;
    width: 40px;
    height: 40px;
    object-fit: none;
    cursor: ${props => (props.enabled ? 'pointer' : 'default')};
`;

const ImagePrev = styled(ImageNext)`
    transform: scaleX(-1);
`;

export default ReaderCollapsableHeader;
